import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  FileText,
  Download,
  Edit2,
  Save,
  Loader2,
  Package,
  DollarSign,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { InvoicePriceSource, Order } from '../types';
import { toast } from 'sonner';
import { generateAndDownloadInvoice, EnhancedProductData } from '../utils/invoiceGenerator';
import { fetchProductById, searchProducts } from '../services/productService';

interface InvoicePreviewModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

interface EditableItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  convertedPrice: number | null; // Price based on selected invoice source (nullable for missing JMD)
  hsCode: string;
  originCountry: string;
  brand?: string;
  netWeight?: number;
  weightUnit?: string;
}

export function InvoicePreviewModal({ order, isOpen, onClose }: InvoicePreviewModalProps) {
  const [items, setItems] = useState<EditableItem[]>([]);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productData, setProductData] = useState<EnhancedProductData>({});
  const [priceSource, setPriceSource] = useState<InvoicePriceSource>('order');
  const isJamaicanPricing = priceSource === 'jamaican';

  const enforceMinimumPrice = (value: number): number => {
    const rounded = Number(value.toFixed(2));
    return rounded > 0 ? rounded : 0.01;
  };

  useEffect(() => {
    if (isOpen && order) {
      loadProductData();
    } else {
      resetState();
    }
  }, [isOpen, order]);

  const resetState = () => {
    setItems([]);
    setEditingItemId(null);
    setIsGenerating(false);
    setProductData({});
    setPriceSource('order');
  };

  const loadProductData = async () => {
    if (!order) return;
    
    try {
      setIsLoadingProducts(true);
      const enhancedData: EnhancedProductData = {};
      
      for (const item of order.items) {
        try {
          let product = await fetchProductById(item.id);
          
          if (!product) {
            const searchResults = await searchProducts(item.name.trim());
            product = searchResults.products.find(p => p.name.trim().toLowerCase() === item.name.trim().toLowerCase()) || null;
          }
          
          if (product) {
            enhancedData[item.id] = product;
          }
        } catch (error) {
          console.warn(`Could not fetch product data for item ${item.id}:`, error);
        }
      }
      
      setProductData(enhancedData);
    } catch (error) {
      console.error('Error loading product data:', error);
      toast.error('Failed to load product data');
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleItemChange = (itemId: string, field: keyof EditableItem, value: any) => {
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const reverseConvertPrice = (convertedPrice: number): number => {
    const currency = getOrderCurrency();
    const rates = getOrderExchangeRates();
    const sanitizedConverted = enforceMinimumPrice(convertedPrice);

    if (currency === 'USD') {
      return sanitizedConverted;
    }

    const rate = rates[currency] || 1;
    return Number((sanitizedConverted / rate).toFixed(2));
  };

  const handleGenerate = async () => {
    if (!order) return;

    try {
      setIsGenerating(true);

      const sanitizedConvertedPrices = priceSource === 'order'
        ? items.map(item => enforceMinimumPrice(item.convertedPrice ?? 0))
        : undefined;

      // Create updated order with edited values
      const updatedOrder = {
        ...order,
        items: order.items.map((originalItem, index) => {
          const editedItem = items[index];
          const baseUpdates = {
            ...originalItem,
            hsCode: editedItem.hsCode,
            originCountry: editedItem.originCountry,
          };

          if (priceSource !== 'order' || !sanitizedConvertedPrices) {
            return baseUpdates;
          }

          // Convert the edited converted price back to USD for storage
          const sanitizedConvertedPrice = sanitizedConvertedPrices[index];
          const updatedUsdPrice = reverseConvertPrice(sanitizedConvertedPrice);

          return {
            ...baseUpdates,
            price: enforceMinimumPrice(updatedUsdPrice),
            onSalePrice: originalItem.onSale ? enforceMinimumPrice(updatedUsdPrice) : undefined,
          };
        }),
      };

      await generateAndDownloadInvoice({ 
        order: updatedOrder,
        productData,
        convertedPrices: sanitizedConvertedPrices,
        priceSource,
      });
      
      toast.success('Invoice generated successfully!');
      onClose();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error(
        error instanceof Error 
          ? error.message 
          : 'Failed to generate invoice'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const getOrderCurrency = (): string => {
    return order?.purchaseCurrency || 'USD';
  };

  const getOrderExchangeRates = () => {
    return order?.exchangeRateSnapshot || { USD: 1, CAD: 1.35, GBP: 0.8 };
  };

  const getConvertedItemPriceFromOrder = (usdPrice: number): number => {
    const currency = getOrderCurrency();
    const rates = getOrderExchangeRates();
    let converted = usdPrice;

    if (currency !== 'USD') {
      const rate = rates[currency] || 1;
      converted = Math.ceil(usdPrice * rate);
    }

    return enforceMinimumPrice(converted);
  };

  const buildEditableItems = (
    source: InvoicePriceSource,
    data: EnhancedProductData,
    currentOrder: Order
  ): EditableItem[] => {
    return currentOrder.items.map(item => {
      const product = data[item.id];
      const usdPrice = item.onSale && item.onSalePrice ? item.onSalePrice : item.price;
      const convertedPrice = source === 'jamaican'
        ? (typeof product?.jamaicanPrice === 'number' ? enforceMinimumPrice(product.jamaicanPrice) : null)
        : enforceMinimumPrice(getConvertedItemPriceFromOrder(usdPrice));

      return {
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: enforceMinimumPrice(usdPrice),
        convertedPrice,
        hsCode: product?.hsCode || item.hsCode || '',
        originCountry: product?.originCountry || item.originCountry || 'JM',
        brand: product?.brand,
        netWeight: product?.netWeight,
        weightUnit: product?.weightUnit,
      };
    });
  };

  useEffect(() => {
    if (!isOpen || !order || isLoadingProducts) return;
    setItems(buildEditableItems(priceSource, productData, order));
  }, [isOpen, order, priceSource, productData, isLoadingProducts]);

  const extractSetQuantity = (name: string): number => {
    const setMatch = name.match(/\(Set of (\d+)\)/i);
    return setMatch ? parseInt(setMatch[1], 10) : 1;
  };

  const cleanProductName = (name: string): string => {
    return name.replace(/\s*\(Set of \d+\)\s*/gi, '').trim();
  };

  const calculateDisplayQuantity = (item: EditableItem): number => {
    const setQuantity = extractSetQuantity(item.name);
    return item.quantity * setQuantity;
  };

  const calculateDisplayUnitPrice = (item: EditableItem): number | null => {
    if (item.convertedPrice === null) return null;
    const setQuantity = extractSetQuantity(item.name);
    const perUnit = setQuantity > 1 
      ? Number((item.convertedPrice / setQuantity).toFixed(2))
      : item.convertedPrice;
    return enforceMinimumPrice(perUnit);
  };

  const handleUnitPriceChange = (itemId: string, newDisplayUnitPrice: number) => {
    if (isJamaicanPricing) return;
    setItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      
      const setQuantity = extractSetQuantity(item.name);
      const sanitizedUnitPrice = enforceMinimumPrice(newDisplayUnitPrice);
      // If it's a set, multiply the per-item price by set quantity to get the full item price
      const newConvertedPrice = setQuantity > 1 
        ? sanitizedUnitPrice * setQuantity 
        : sanitizedUnitPrice;
      
      return { ...item, convertedPrice: enforceMinimumPrice(newConvertedPrice) };
    }));
  };

  const calculateSubtotal = (item: EditableItem): number | null => {
    if (item.convertedPrice === null) return null;
    const subtotal = Number((item.convertedPrice * item.quantity).toFixed(2));
    return enforceMinimumPrice(subtotal);
  };

  const getTotalValue = (): number | null => {
    const subtotals = items.map(item => calculateSubtotal(item));
    if (isJamaicanPricing && subtotals.some((subtotal) => subtotal === null)) {
      return null;
    }
    const total = subtotals.reduce((sum, subtotal) => sum + (subtotal ?? 0), 0);
    return Number(total.toFixed(2));
  };

  if (!order) return null;

  const currency = isJamaicanPricing ? 'JMD' : getOrderCurrency();
  const totalValue = getTotalValue();

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
          style={{
            margin: 0,
            padding: '1rem',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: 'spring',
              duration: 0.5,
              bounce: 0.3,
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-indigo-600 to-indigo-700 p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Invoice Preview</h2>
                    <p className="text-indigo-100 text-sm">Order {order.orderNumber}</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                  disabled={isGenerating}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {isLoadingProducts ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
                  <p className="text-gray-600">Loading product data...</p>
                </div>
              ) : (
                <>
                  {/* Order Info */}
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Package className="w-4 h-4" />
                        <span className="font-medium">Customer:</span>
                        <span>{order.customer.name}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {currency}
                      </Badge>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Total Items:</span>
                      <span className="font-semibold">{order.items.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Price Source:</span>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={priceSource === 'order' ? 'default' : 'outline'}
                          onClick={() => setPriceSource('order')}
                          className="h-8"
                        >
                          Order Currency
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={priceSource === 'jamaican' ? 'default' : 'outline'}
                          onClick={() => setPriceSource('jamaican')}
                          className="h-8"
                        >
                          Product JMD
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Invoice Value:</span>
                      <span className="font-semibold text-indigo-600">
                        {totalValue === null ? '-' : `${totalValue.toFixed(2)} ${currency}`}
                      </span>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Invoice Items</h3>
                      <p className="text-sm text-gray-500">Click fields to edit</p>
                    </div>

                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      {/* Table Header */}
                      <div className="bg-gray-50 px-4 py-3 grid grid-cols-12 gap-3 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                        <div className="col-span-3">Product</div>
                        <div className="col-span-1 text-center">Qty</div>
                        <div className="col-span-2 text-center">Unit Price</div>
                        <div className="col-span-2 text-center">HS Code</div>
                        <div className="col-span-2 text-center">COO</div>
                        <div className="col-span-2 text-right">Subtotal</div>
                      </div>

                      {/* Table Body */}
                      <div className="divide-y divide-gray-200">
                        {items.map((item, index) => {
                          const isEditing = editingItemId === item.id;
                          const displayQty = calculateDisplayQuantity(item);
                          const displayUnitPrice = calculateDisplayUnitPrice(item);
                          const subtotal = calculateSubtotal(item);
                          const cleanedName = cleanProductName(item.name);

                          return (
                            <div
                              key={item.id}
                              className="px-4 py-4 grid grid-cols-12 gap-3 items-center hover:bg-gray-50 transition-colors"
                            >
                              {/* Product Name & Brand */}
                              <div className="col-span-3">
                                <div className="space-y-1">
                                  {item.brand && (
                                    <p className="text-xs font-semibold text-indigo-600 uppercase">
                                      {item.brand}
                                    </p>
                                  )}
                                  <p className="text-sm font-medium text-gray-900">
                                    {cleanedName}
                                  </p>
                                  {item.netWeight && item.weightUnit && (
                                    <p className="text-xs text-gray-500">
                                      {item.netWeight}{item.weightUnit.toLowerCase()}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Quantity */}
                              <div className="col-span-1 text-center">
                                <div className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-lg">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {displayQty}
                                  </span>
                                </div>
                              </div>

                              {/* Unit Price */}
                              <div className="col-span-2">
                                {isJamaicanPricing ? (
                                  <div className="text-center">
                                    {displayUnitPrice === null ? (
                                      <span className="text-sm text-gray-400">-</span>
                                    ) : (
                                      <>
                                        <p className="text-sm font-semibold text-gray-900">
                                          {displayUnitPrice.toFixed(2)}
                                        </p>
                                        <p className="text-xs text-gray-500">{currency}</p>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="relative">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={displayUnitPrice !== null ? displayUnitPrice.toFixed(2) : ''}
                                      onChange={(e) => handleUnitPriceChange(item.id, parseFloat(e.target.value) || 0)}
                                      className="text-center text-sm h-8 pr-10"
                                      placeholder="0.00"
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                                      {currency}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* HS Code */}
                              <div className="col-span-2">
                                <Input
                                  value={item.hsCode}
                                  onChange={(e) => handleItemChange(item.id, 'hsCode', e.target.value)}
                                  className="text-center text-sm h-8"
                                  placeholder="-"
                                />
                              </div>

                              {/* Country of Origin */}
                              <div className="col-span-2">
                                <Input
                                  value={item.originCountry}
                                  onChange={(e) => handleItemChange(item.id, 'originCountry', e.target.value.toUpperCase())}
                                  className="text-center text-sm h-8 uppercase"
                                  maxLength={2}
                                  placeholder="JM"
                                />
                              </div>

                              {/* Subtotal */}
                              <div className="col-span-2 text-right">
                                {subtotal === null ? (
                                  <p className="text-sm font-semibold text-gray-400">-</p>
                                ) : (
                                  <>
                                    <p className="text-sm font-semibold text-gray-900">
                                      {subtotal.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-gray-500">{currency}</p>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Table Footer */}
                      <div className="bg-gray-50 px-4 py-3 border-t-2 border-gray-300">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-700">Total Invoice Value:</span>
                          <div className="text-right">
                            <p className="text-lg font-bold text-indigo-600">
                              {totalValue === null ? '-' : `${totalValue.toFixed(2)} ${currency}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FileText className="w-4 h-4" />
                  <span>4×6 inch thermal label format</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={isGenerating}
                    className="min-w-[100px]"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleGenerate}
                    disabled={isGenerating || isLoadingProducts}
                    className="min-w-[140px] bg-indigo-600 hover:bg-indigo-700"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Generate Invoice
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
